import React, { useEffect, useMemo, useState } from 'react'
import { api, formatCurrency } from '../../utils/api'
import { ConsignmentBadge } from '../shared'

// ---------------------------------------------------------------------------
// Pricing Calendar — READ-ONLY forward forecast from the engine
// (GET /api/pricing/forecast). Each tick is a monthly age anniversary where the
// curve steps; the backend recomputes on demand (spec §5.1.8). There is no
// fortnightly zig-zag and no weekday/week-of-month schedule — the engine prices
// purely by stock age in whole months, so the calendar reflects that.
// ---------------------------------------------------------------------------

const MS_PER_DAY = 86400000

// Brand colours per audiotrader-prism/infrastructure/superset/shared/label_colors.yaml
const CHANNELS = {
  shopify: { dot: '#96BF48', bg: 'rgba(150, 191, 72, 0.20)', label: 'D2C' },
  reverb:  { dot: '#E4002B', bg: 'rgba(228,   0, 43, 0.18)', label: 'Reverb' },
  ebay:    { dot: '#0064D2', bg: 'rgba(  0, 100, 210, 0.18)', label: 'eBay' },
}
const CHANNEL_KEYS = ['shopify', 'reverb', 'ebay']

function addDays(date, days) { return new Date(date.getTime() + days * MS_PER_DAY) }
function isoDate(date) { return date.toISOString().slice(0, 10) }

function startOfWeek(date) {
  const d = new Date(date)
  const day = d.getUTCDay()
  const diff = day === 0 ? 6 : day - 1 // Monday start
  d.setUTCDate(d.getUTCDate() - diff)
  d.setUTCHours(0, 0, 0, 0)
  return d
}

function formatPence(cents) { return cents == null ? '—' : formatCurrency(cents / 100) }

function deltaPct(t) {
  return t.current_cents > 0 ? ((t.projected_cents - t.current_cents) / t.current_cents) * 100 : 0
}

function downloadCsv(rows) {
  const header = ['tick_date', 'shopify_product_id', 'title', 'product_type', 'channel', 'age_months', 'current_cents', 'projected_cents', 'delta_pct']
  const esc = v => {
    const s = v == null ? '' : String(v)
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
  }
  const lines = [header.join(',')]
  for (const t of rows) {
    lines.push([t.tick_date, t.shopify_product_id, esc(t.title), esc(t.product_type), t.channel, t.age_months_at_tick, t.current_cents, t.projected_cents, deltaPct(t).toFixed(1)].join(','))
  }
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `pricing-forecast-${isoDate(new Date())}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

function PricingCalendar() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const [selectedDates, setSelectedDates] = useState(new Set())
  const [searchTerm, setSearchTerm] = useState('')

  useEffect(() => {
    let alive = true
    // D2C ticks only (operator call 2026-07-14): the forecast's marketplace
    // values use the curve model's flat uplift, but the real fan-out prices
    // eBay/Reverb with the tiered ChannelPricingService — right dates, wrong
    // numbers. Until that consolidation (§6.5), showing them would mislead.
    api.get('/api/pricing/forecast')
      .then(d => { if (alive) setData({ ...d, ticks: (d?.ticks || []).filter(t => t.channel === 'shopify') }) })
      .catch(e => { if (alive) setError(e.message || 'Failed to load forecast') })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [])

  const today = useMemo(() => { const d = new Date(); d.setUTCHours(0, 0, 0, 0); return d }, [])
  const horizonDays = data?.horizon_days || 56

  const weeks = useMemo(() => {
    const start = startOfWeek(today)
    const weeksAhead = Math.ceil(horizonDays / 7) + 1
    const out = []
    for (let w = 0; w < weeksAhead; w++) {
      const weekStart = addDays(start, w * 7)
      const days = []
      for (let d = 0; d < 7; d++) { const date = addDays(weekStart, d); days.push({ date, iso: isoDate(date) }) }
      out.push({ weekStart, days })
    }
    return out
  }, [today, horizonDays])

  const ticksByDate = useMemo(() => {
    const map = new Map()
    for (const t of data?.ticks || []) {
      if (!map.has(t.tick_date)) map.set(t.tick_date, [])
      map.get(t.tick_date).push(t)
    }
    return map
  }, [data])

  // Search filter applied to every (D2C) tick (drives the CSV export).
  const filteredAll = useMemo(() => {
    const term = searchTerm.trim().toLowerCase()
    return (data?.ticks || []).filter(t =>
      !term || (t.title || '').toLowerCase().includes(term)
    )
  }, [data, searchTerm])

  // The table restricts the filtered ticks to the days the operator selected.
  const visibleTicks = useMemo(() => {
    if (selectedDates.size === 0) return []
    return filteredAll
      .filter(t => selectedDates.has(t.tick_date))
      .sort((a, b) => a.tick_date.localeCompare(b.tick_date) || a.channel.localeCompare(b.channel))
  }, [filteredAll, selectedDates])

  function toggleDate(iso) {
    setSelectedDates(prev => { const n = new Set(prev); n.has(iso) ? n.delete(iso) : n.add(iso); return n })
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Loading…</div>
  if (error) return <div style={{ padding: 40, textAlign: 'center', color: '#ef4444' }}>{error}</div>

  const totalTicks = data?.ticks?.length || 0

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18 }}>Pricing Calendar</h2>
          <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: 13 }}>
            Upcoming D2C price changes over the next {horizonDays} days — each item reprices on its monthly
            age anniversary. Read-only forecast, recomputed on demand; the nightly job (02:35 UTC, capped
            per run) makes the actual pushes, down-only. eBay &amp; Reverb follow each D2C change via the
            channel fan-out at their own markup, so they are not shown separately.
          </p>
        </div>
        <button className="btn btn-primary" style={{ fontSize: 12 }} disabled={filteredAll.length === 0}
          onClick={() => downloadCsv(filteredAll)}>
          Download CSV ({filteredAll.length})
        </button>
      </div>

      {/* Heatmap */}
      <div className="card" style={{ padding: 12, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div style={{ fontSize: 12, color: '#6b7280' }}>
            {totalTicks} upcoming change{totalTicks === 1 ? '' : 's'} · click a day to view its ticks · click multiple to combine
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {CHANNEL_KEYS.map(ch => (
              <span key={ch} style={{ fontSize: 11, color: '#6b7280', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: CHANNELS[ch].dot }} />
                {CHANNELS[ch].label}
              </span>
            ))}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '120px repeat(7, 1fr)', gap: 4, fontSize: 11 }}>
          <div />
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
            <div key={d} style={{ textAlign: 'center', color: '#6b7280', fontWeight: 500 }}>{d}</div>
          ))}
          {weeks.map(week => (
            <React.Fragment key={week.weekStart.toISOString()}>
              <div style={{ color: '#9ca3af', fontSize: 11, alignSelf: 'center' }}>
                Week of {week.weekStart.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', timeZone: 'UTC' })}
              </div>
              {week.days.map(({ date, iso }) => {
                const ticks = ticksByDate.get(iso) || []
                const counts = { shopify: 0, reverb: 0, ebay: 0 }
                for (const t of ticks) counts[t.channel]++
                const isSelected = selectedDates.has(iso)
                const isToday = iso === isoDate(today)
                const hasContent = ticks.length > 0
                return (
                  <button key={iso} onClick={() => toggleDate(iso)} disabled={!hasContent}
                    style={{
                      padding: 6, borderRadius: 4,
                      border: isSelected ? '2px solid #3b82f6' : isToday ? '1px solid #6b7280' : '1px solid var(--border-color)',
                      background: isSelected ? 'rgba(59, 130, 246, 0.12)' : hasContent ? 'var(--bg-secondary)' : 'transparent',
                      cursor: hasContent ? 'pointer' : 'default', opacity: hasContent ? 1 : 0.4, textAlign: 'left', minHeight: 52,
                    }}>
                    <div style={{ fontSize: 10, color: '#6b7280', marginBottom: 4 }}>{date.getUTCDate()}</div>
                    {hasContent ? (
                      <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                        {CHANNEL_KEYS.map(ch => counts[ch] > 0 && (
                          <span key={ch} style={{ fontSize: 10, padding: '1px 5px', borderRadius: 3, background: CHANNELS[ch].bg, color: CHANNELS[ch].dot, fontWeight: 600 }}>
                            {CHANNELS[ch].label[0]} {counts[ch]}
                          </span>
                        ))}
                      </div>
                    ) : <div style={{ fontSize: 10, color: '#4b5563' }}>·</div>}
                  </button>
                )
              })}
            </React.Fragment>
          ))}
        </div>
      </div>

      {totalTicks === 0 && (
        <div className="card" style={{ padding: 30, textAlign: 'center', color: '#6b7280', fontSize: 13 }}>
          No upcoming price changes in the next {horizonDays} days. Items reprice on their monthly age
          anniversary (month 1 = the listing month) until they reach the M12 floor.
        </div>
      )}

      {/* Ticks list */}
      {selectedDates.size > 0 && (
        <div className="card" style={{ padding: 0 }}>
          <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border-color)', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <input type="text" placeholder="Search by product name..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              style={{ width: 220, padding: '6px 10px', borderRadius: 4, border: '1px solid var(--border-color)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: 12 }} />
            <span style={{ color: '#6b7280', fontSize: 11 }}>{selectedDates.size} day(s) · {visibleTicks.length} tick(s)</span>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table className="table" style={{ fontSize: 12 }}>
              <thead>
                <tr>
                  <th style={{ width: 100 }}>Tick date</th>
                  <th>Product</th>
                  <th style={{ width: 70 }}>Channel</th>
                  <th style={{ width: 55 }}>Age</th>
                  <th style={{ width: 90 }}>From</th>
                  <th style={{ width: 90 }}>To</th>
                  <th style={{ width: 65 }}>Δ%</th>
                </tr>
              </thead>
              <tbody>
                {visibleTicks.map((t, idx) => {
                  const dp = deltaPct(t)
                  return (
                    <tr key={`${t.shopify_product_id}-${t.channel}-${t.tick_date}-${idx}`}>
                      <td style={{ color: '#6b7280' }}>{t.tick_date}</td>
                      <td>
                        <div style={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}>
                          {t.title || t.shopify_product_id}
                          {t.is_consignment && <ConsignmentBadge title="Consignment — descending on its markdown curve" />}
                        </div>
                        <div style={{ color: '#6b7280', fontSize: 10 }}>{t.product_type}</div>
                      </td>
                      <td>
                        <span style={{ background: CHANNELS[t.channel].bg, color: CHANNELS[t.channel].dot, padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 500 }}>
                          {CHANNELS[t.channel].label}
                        </span>
                      </td>
                      <td style={{ color: '#6b7280' }}>M{t.age_months_at_tick}</td>
                      <td style={{ color: '#6b7280' }}>{formatPence(t.current_cents)}</td>
                      <td style={{ color: dp < 0 ? '#22c55e' : dp > 0 ? '#f59e0b' : '#6b7280', fontWeight: 600 }}>{formatPence(t.projected_cents)}</td>
                      <td style={{ color: dp < 0 ? '#22c55e' : dp > 0 ? '#f59e0b' : '#6b7280' }}>{dp > 0 ? '+' : ''}{dp.toFixed(1)}%</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {visibleTicks.length === 0 && (
            <div style={{ padding: 30, textAlign: 'center', color: '#6b7280', fontSize: 12 }}>No ticks match the current filters.</div>
          )}
        </div>
      )}
    </div>
  )
}

export default PricingCalendar
